import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { PillarScopeType } from '@/constants/PillarScope';
import type { User } from '@/types/directory/User';

import { useGetDirectReports, useGetUserById } from '@/api/directory';
import { PAGE_PARAM } from '@/constants/ObjectivePeriodLens';
import { PILLAR_SCOPE_PARAM, PillarScope } from '@/constants/PillarScope';
import { useReplaceSearch } from '@/hooks/utils/useReplaceSearch';
import {
    clearListFilters,
    hasActiveFilterIn,
    readManagerIds,
    writeManagerIds,
} from '@/utils/objectiveListAddress';

const NO_REPORTS: User[] = [];

const readRequestedScope = (params: URLSearchParams): PillarScopeType =>
    Object.values(PillarScope).find((scope) => scope === params.get(PILLAR_SCOPE_PARAM)) ??
    PillarScope.ORGANIZATION;

/** Whether the list is narrowed to the reader's own line: the manager filter on the reader alone. */
const holdsTeamFilter = (params: URLSearchParams, readerId: string | undefined): boolean => {
    const managerIds = readManagerIds(params);

    return !!readerId && managerIds.length === 1 && managerIds[0] === readerId;
};

/** Whether a filter other than the manager filter is in effect. */
const hasFilterBeyondManagers = (params: URLSearchParams): boolean => {
    const beyondManagers = new URLSearchParams(params);
    writeManagerIds(beyondManagers, []);

    return hasActiveFilterIn(beyondManagers);
};

/**
 * Whose objectives the pillars are counted among. "My team" (the reader and their direct reports,
 * offered to a reader who has some) sets the list's manager filter on the reader, so the list and
 * the pillars count the same people, and holds while that filter does. "Selected filters" is
 * offered while a filter is in effect -- under "my team", only once a filter beyond the team's own
 * joins it, which turns the pillars to it. A scope asked for but not offered reads as
 * "organization".
 *
 * A team asked for that is not the reader's own -- a "my team" link shared by another manager --
 * falls back to the organization once the reporting line is known, and the manager filter it came
 * with leaves the address: the list would otherwise keep showing someone else's team.
 */
export const usePillarScope = () => {
    const { search } = useLocation();
    const replaceParams = useReplaceSearch();
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const requested = readRequestedScope(params);
    const hasActiveFilter = hasActiveFilterIn(params);

    const {
        data: reader,
        isLoading: isResolvingReader,
        isSuccess: isReaderKnown,
    } = useGetUserById({ userId: 'me' });
    const {
        data: directReports = NO_REPORTS,
        isLoading: isResolvingReports,
        isSuccess: areReportsKnown,
    } = useGetDirectReports();
    const currentUserId = reader?.id;

    const isTeamOffered = directReports.length > 0 && !!currentUserId;
    const teamIds = useMemo(
        () => new Set(currentUserId ? [currentUserId, ...directReports.map(({ id }) => id)] : []),
        [currentUserId, directReports],
    );

    const scope = useMemo<PillarScopeType>(() => {
        if (requested === PillarScope.FILTERS && hasActiveFilter) {
            return PillarScope.FILTERS;
        }

        return requested === PillarScope.TEAM &&
            isTeamOffered &&
            holdsTeamFilter(params, currentUserId)
            ? PillarScope.TEAM
            : PillarScope.ORGANIZATION;
    }, [requested, hasActiveFilter, isTeamOffered, params, currentUserId]);

    const asksForAnotherTeam =
        requested === PillarScope.TEAM &&
        isReaderKnown &&
        areReportsKnown &&
        !!currentUserId &&
        scope !== PillarScope.TEAM;

    useEffect(() => {
        if (asksForAnotherTeam) {
            replaceParams((address) => {
                address.delete(PILLAR_SCOPE_PARAM);
                address.delete(PAGE_PARAM);
                writeManagerIds(address, []);
            });
        }
    }, [asksForAnotherTeam, replaceParams]);

    // Under "my team", the manager filter on the reader is the team itself, not a selection of
    // filters: "selected filters" is offered once another filter joins it.
    const isFiltersOffered =
        scope === PillarScope.TEAM ? hasFilterBeyondManagers(params) : hasActiveFilter;

    // "My team" and "organization" name their people themselves, so a click on either takes the
    // list's filters off the address, the page with them: a filter left in the toolbar while the
    // list and the pillars ignored it read as a filter that failed. The period is not a filter and stays.
    const setScope = useCallback(
        (next: PillarScopeType) =>
            replaceParams((address) => {
                address.set(PILLAR_SCOPE_PARAM, next);

                if (next === PillarScope.FILTERS) {
                    return;
                }

                clearListFilters(address);
                address.delete(PAGE_PARAM);

                if (next === PillarScope.TEAM && currentUserId) {
                    writeManagerIds(address, [currentUserId]);
                }
            }),
        [replaceParams, currentUserId],
    );

    /**
     * Written with each change of the list's filters: whoever changes a filter wants to see what it
     * changes. Filters that come down to the team's own, under "my team" or "selected filters",
     * read as the team; otherwise any filter in effect turns the pillars to "selected filters", and
     * none turns them to the organization.
     */
    const followFilters = useCallback(
        (next: URLSearchParams) => {
            const comesDownToTeam =
                readRequestedScope(next) !== PillarScope.ORGANIZATION &&
                isTeamOffered &&
                holdsTeamFilter(next, currentUserId) &&
                !hasFilterBeyondManagers(next);

            if (comesDownToTeam) {
                next.set(PILLAR_SCOPE_PARAM, PillarScope.TEAM);
            } else if (hasActiveFilterIn(next)) {
                next.set(PILLAR_SCOPE_PARAM, PillarScope.FILTERS);
            } else {
                next.delete(PILLAR_SCOPE_PARAM);
            }
        },
        [isTeamOffered, currentUserId],
    );

    return {
        scope,
        setScope,
        followFilters,
        isTeamOffered,
        isFiltersOffered,
        teamIds,
        /** Asked for the team while the reporting line is still on its way: nothing to show yet. */
        isResolvingScope:
            requested === PillarScope.TEAM && (isResolvingReader || isResolvingReports),
    };
};
